import { CheckCircle2, Heart, Home, Receipt, Repeat, ShieldCheck, Split, Users } from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Head } from 'vite-react-ssg'
import { Link } from 'react-router-dom'
import { Logo } from '../branding/Logo'
import { StoreBadges } from '../StoreBadges'
import { SEO } from './SEO'

export type SeoIcon = 'check' | 'shield' | 'heart' | 'split' | 'receipt' | 'calendar' | 'home' | 'kids'

const ICONS: Record<SeoIcon, React.ComponentType<{ className?: string }>> = {
  check: CheckCircle2,
  shield: ShieldCheck,
  heart: Heart,
  split: Split,
  receipt: Receipt,
  calendar: Repeat,
  home: Home,
  kids: Users,
}

export type SeoPageContent = {
  locale: 'de' | 'en'
  path: string
  alternatePath: string
  seo: {
    title: string
    description: string
  }
  hero: {
    eyebrow?: string
    h1: string
    sub: string
    note?: string
  }
  problems: {
    heading: string
    intro?: string
    items: { title: string; description: string }[]
  }
  solutions: {
    heading: string
    intro?: string
    items: { icon: SeoIcon; title: string; description: string }[]
  }
  comparison?: {
    heading: string
    intro?: string
    columns: [string, string]
    rows: { topic: string; without: string; with: string }[]
  }
  trust: {
    heading: string
    intro?: string
    items: { title: string; description: string }[]
  }
  relatedBlog: {
    heading: string
    intro?: string
    posts: { title: string; description: string; href: string }[]
  }
  faq: {
    heading: string
    items: { question: string; answer: string }[]
  }
  finalCta: {
    heading: string
    sub?: string
  }
  uiStrings: {
    backToHome: string
    related: string
    legalPrivacy: string
    legalTerms: string
    legalImprint: string
    legalSupport: string
    copyrightRights: string
    breadcrumbHome: string
    breadcrumbCurrent: string
  }
}

const SITE_URL = 'https://equna.eu'

function SeoLanguageSwitcher({ locale, alternatePath }: { locale: 'de' | 'en'; alternatePath: string }) {
  const { i18n } = useTranslation()

  const handleSwitch = () => {
    const target = locale === 'de' ? 'en' : 'de'
    i18n.changeLanguage(target)
  }

  return (
    <Link
      to={alternatePath}
      onClick={handleSwitch}
      className="flex items-center gap-2 px-3 py-1.5 text-sm font-semibold rounded-full border border-slate-200 hover:border-[#237CCA] hover:text-[#237CCA] transition-colors font-nunito"
      aria-label="Toggle Language"
    >
      <span className={locale === 'de' ? 'font-bold text-[#237CCA]' : 'text-slate-400'}>DE</span>
      <span className="text-slate-300">|</span>
      <span className={locale === 'en' ? 'font-bold text-[#237CCA]' : 'text-slate-400'}>EN</span>
    </Link>
  )
}

export function SeoLandingLayout({ content }: { content: SeoPageContent }) {
  const { i18n } = useTranslation()
  const canonicalUrl = `${SITE_URL}${content.path}`
  const alternateUrl = `${SITE_URL}${content.alternatePath}`

  // Sync i18n state with URL locale so child components (StoreBadges) pick the right language
  useEffect(() => {
    if (i18n.language?.toLowerCase().slice(0, 2) !== content.locale) {
      i18n.changeLanguage(content.locale)
    }
  }, [content.locale, i18n])

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${canonicalUrl}#webpage`,
        url: canonicalUrl,
        name: content.seo.title,
        description: content.seo.description,
        inLanguage: content.locale === 'de' ? 'de-DE' : 'en-US',
        isPartOf: { '@id': `${SITE_URL}/#website` },
        breadcrumb: { '@id': `${canonicalUrl}#breadcrumb` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonicalUrl}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: content.uiStrings.breadcrumbHome,
            item: `${SITE_URL}/`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: content.uiStrings.breadcrumbCurrent,
            item: canonicalUrl,
          },
        ],
      },
      {
        '@type': 'FAQPage',
        '@id': `${canonicalUrl}#faq`,
        mainEntity: content.faq.items.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
    ],
  }

  return (
    <div className="min-h-screen bg-slate-50 font-nunito text-slate-900">
      <SEO
        title={content.seo.title}
        description={content.seo.description}
        canonical={content.path}
        jsonLd={jsonLd}
      />
      <Head>
        <html lang={content.locale} />
        <link rel="alternate" hrefLang={content.locale} href={canonicalUrl} />
        <link rel="alternate" hrefLang={content.locale === 'de' ? 'en' : 'de'} href={alternateUrl} />
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
      </Head>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 border-b border-slate-200 backdrop-blur-md">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <Logo size="md" variant="image" />
            <div className="flex items-center">
              <span className="text-lg font-bold" style={{ color: '#237CCA' }}>Eq</span>
              <span className="text-lg font-bold" style={{ color: '#10B981' }}>una</span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <SeoLanguageSwitcher locale={content.locale} alternatePath={content.alternatePath} />
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 font-semibold text-sm transition-colors"
            >
              {content.uiStrings.backToHome}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-blue-50 via-slate-50 to-emerald-50 pt-16 pb-20 md:pt-24 md:pb-28">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            {content.hero.eyebrow ? (
              <span className="inline-block px-3 py-1 mb-6 rounded-full bg-white border border-blue-100 text-[#237CCA] text-xs font-bold tracking-wide uppercase">
                {content.hero.eyebrow}
              </span>
            ) : null}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight tracking-tight mb-6">
              {content.hero.h1}
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 max-w-2xl mx-auto">
              {content.hero.sub}
            </p>
            <div className="flex flex-col items-center gap-4">
              <StoreBadges className="justify-center" />
              {content.hero.note ? (
                <p className="text-sm text-slate-500">{content.hero.note}</p>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      {/* Problem block */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{content.problems.heading}</h2>
              {content.problems.intro ? (
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">{content.problems.intro}</p>
              ) : null}
            </div>
            <ul className="grid sm:grid-cols-2 gap-4">
              {content.problems.items.map((item) => (
                <li key={item.title} className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
                  <h3 className="font-bold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{item.description}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Solution block */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{content.solutions.heading}</h2>
              {content.solutions.intro ? (
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">{content.solutions.intro}</p>
              ) : null}
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {content.solutions.items.map((item) => {
                const Icon = ICONS[item.icon]
                return (
                  <div key={item.title} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                    <div className="w-12 h-12 rounded-full bg-[#237CCA]/10 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-[#237CCA]" />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{item.description}</p>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Comparison table (optional) */}
      {content.comparison ? (
        <section className="py-16 md:py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{content.comparison.heading}</h2>
                {content.comparison.intro ? (
                  <p className="text-lg text-slate-600 max-w-2xl mx-auto">{content.comparison.intro}</p>
                ) : null}
              </div>
              <div className="overflow-x-auto rounded-2xl border border-slate-200">
                <table className="w-full text-left">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-sm font-semibold text-slate-700">&nbsp;</th>
                      <th className="px-4 py-3 text-sm font-semibold text-slate-700">{content.comparison.columns[0]}</th>
                      <th className="px-4 py-3 text-sm font-semibold text-[#237CCA]">{content.comparison.columns[1]}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {content.comparison.rows.map((row, idx) => (
                      <tr key={row.topic} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="px-4 py-3 text-sm font-semibold text-slate-900 align-top">{row.topic}</td>
                        <td className="px-4 py-3 text-sm text-slate-600 align-top">{row.without}</td>
                        <td className="px-4 py-3 text-sm text-slate-700 align-top">{row.with}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* Trust block */}
      <section className="py-16 md:py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{content.trust.heading}</h2>
              {content.trust.intro ? (
                <p className="text-lg text-slate-300 max-w-2xl mx-auto">{content.trust.intro}</p>
              ) : null}
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {content.trust.items.map((item) => (
                <div key={item.title} className="bg-slate-800/60 rounded-2xl p-5 border border-slate-700">
                  <h3 className="font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-300 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related blog */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{content.relatedBlog.heading}</h2>
              {content.relatedBlog.intro ? (
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">{content.relatedBlog.intro}</p>
              ) : null}
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {content.relatedBlog.posts.map((post) => (
                <Link
                  key={post.href}
                  to={post.href}
                  className="group bg-white border border-slate-200 rounded-2xl p-5 shadow-sm hover:shadow-lg hover:border-[#237CCA]/40 transition-all"
                >
                  <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-[#237CCA] transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{post.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-10">{content.faq.heading}</h2>
            <div className="space-y-4">
              {content.faq.items.map((item) => (
                <details
                  key={item.question}
                  className="group bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 open:bg-white open:shadow-sm"
                >
                  <summary className="cursor-pointer list-none font-semibold text-slate-900 flex items-center justify-between">
                    {item.question}
                    <span className="ml-4 text-[#237CCA] transition-transform group-open:rotate-45 text-xl leading-none">+</span>
                  </summary>
                  <p className="mt-3 text-slate-600 leading-relaxed">{item.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="py-20"
        style={{ background: 'linear-gradient(to bottom right, #237CCA, #10b981)' }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold">{content.finalCta.heading}</h2>
            {content.finalCta.sub ? (
              <p className="text-lg text-white/90">{content.finalCta.sub}</p>
            ) : null}
            <StoreBadges className="justify-center" variant="dark" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 text-center text-slate-400 text-sm bg-slate-50">
        <p>&copy; {new Date().getFullYear()} Equna. {content.uiStrings.copyrightRights}</p>
        <p className="mt-2">
          <Link to="/datenschutz" className="hover:text-slate-600 transition-colors">{content.uiStrings.legalPrivacy}</Link>
          {' · '}
          <Link to="/agb" className="hover:text-slate-600 transition-colors">{content.uiStrings.legalTerms}</Link>
          {' · '}
          <Link to="/impressum" className="hover:text-slate-600 transition-colors">{content.uiStrings.legalImprint}</Link>
          {' · '}
          <Link to="/support" className="hover:text-slate-600 transition-colors">{content.uiStrings.legalSupport}</Link>
        </p>
      </footer>
    </div>
  )
}
